
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getJwtToken } from '../utils/jwtService';
import glossaryService from '../services/glossaryService';
import { getGlossaryDisplayName, validateDisplayName, validateSharedGlossaryName } from '../utils/glossaryUtils';
import Button from './ui/Button';
import Input from './ui/Input';
import {
    Plus, Edit2, Trash2, Save,
    Book, ArrowLeft,
    CheckCircle, AlertCircle, Loader2, Upload, HelpCircle, X, RefreshCw, Sparkles
} from 'lucide-react';
import AIGlossaryGenerator from './AIGlossaryGenerator';
import DomainLibrary from './DomainLibrary';
import './GlossaryManagement.css';

const LANGUAGES = [
    { code: 'en-US', name: 'English' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    // Add more as needed
];

const GlossaryManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('tenant'); // 'tenant' or 'shared'
    const [view, setView] = useState('list'); // 'list' or 'editor'

    // Data State
    const [glossaries, setGlossaries] = useState([]);
    const [currentGlossary, setCurrentGlossary] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [tenantId, setTenantId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Access control flags - Platform admins (SuperAdmin, IsPlatformAdmin) have full access like Admin
    const isAdmin = user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin';
    const isSuperAdmin = user?.IsPlatformAdmin === true || user?.Role === 'SuperAdmin';
    const userTenantId = user?.TenantId;

    // Redirect if user has no tenant access (not admin and no tenant assigned)
    useEffect(() => {
        if (user && !isAdmin && !userTenantId) {
            navigate('/host');
        }
    }, [user, isAdmin, userTenantId, navigate]);

    // Editor State
    const [editorForm, setEditorForm] = useState({
        name: '', // glossaryName or sharedGlossaryName (for shared glossaries only, hidden for tenant)
        displayName: '', // Display name (visible to users)
        description: '',
        hostInstructions: '',
        entries: []
    });

    // New Entry State
    const [newEntry, setNewEntry] = useState({
        sourceLanguage: 'en-US',
        targetLanguage: 'ko-KR',
        sourceText: '',
        targetText: ''
    });
    const [showImportHelp, setShowImportHelp] = useState(false);

    // Delete confirmation modal state
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { name, displayName }

    // Soft-delete with undo state
    const [deletedEntry, setDeletedEntry] = useState(null); // { data, index }
    const undoTimerRef = useRef(null);

    // Inline entry edit state
    const [editingEntryIdx, setEditingEntryIdx] = useState(null);
    const [editingEntryData, setEditingEntryData] = useState(null);

    // Clear status after 5 seconds
    useEffect(() => {
        if (status.message) {
            const timer = setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    // Load tenants (Admin only) or auto-set tenant for non-admin users
    useEffect(() => {
        const initializeTenantAccess = async () => {
            console.log('[GlossaryManagement] initializeTenantAccess called', {
                hasJwtToken: !!getJwtToken(),
                isAdmin,
                userRole: user?.Role,
                userTenantId
            });

            if (!getJwtToken()) {
                console.log('[GlossaryManagement] No JWT token, skipping tenant load');
                return;
            }

            if (isAdmin) {
                // Admin: Load all tenants for dropdown
                console.log('[GlossaryManagement] Admin detected, loading tenants...');
                try {
                    const data = await glossaryService.getTenants();
                    console.log('[GlossaryManagement] Tenants loaded:', data);
                    setTenants(data);
                } catch (error) {
                    console.error('[GlossaryManagement] Failed to load tenants:', error);
                    setStatus({ type: 'error', message: `Failed to load tenants list: ${error.message}` });
                }
            } else if (userTenantId) {
                // Non-admin with tenant: Auto-select their tenant
                console.log('[GlossaryManagement] Non-admin with tenant:', userTenantId);
                setTenantId(userTenantId);
                // Create a single-item tenant list with user's tenant info
                setTenants([{
                    RowKey: userTenantId,
                    TenantName: user?.TenantName || userTenantId
                }]);
            } else {
                console.log('[GlossaryManagement] Non-admin without tenant');
            }
            // Non-admin without tenant: tenants stays empty, canAccessTenantGlossaries is false
        };
        initializeTenantAccess();
    }, [isAdmin, userTenantId, user?.TenantName, user?.Role]);

    // Load glossaries when tab or tenantId changes
    const loadGlossaries = useCallback(async () => {
        console.log('[GlossaryManagement] loadGlossaries called. hasJwtToken:', !!getJwtToken());
        if (!getJwtToken()) return;
        if (activeTab === 'tenant' && !tenantId) return;
        if (activeTab === 'ai-generator') return;

        setIsLoading(true);
        try {
            let data;
            if (activeTab === 'tenant') {
                const response = await glossaryService.listGlossaries(tenantId);
                data = response['tenant-glossaries'] || [];
            } else {
                const response = await glossaryService.listSharedGlossaries();
                data = response.glossaries || [];
            }
            setGlossaries(data);
        } catch (error) {
            console.error('Failed to load glossaries:', error);
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, tenantId]);

    useEffect(() => {
        loadGlossaries();
    }, [loadGlossaries]);

    const handleRefresh = () => {
        loadGlossaries();
    };

    // Handlers
    const handleCreateNew = () => {
        setEditorForm({
            name: '',
            displayName: '',
            description: '',
            hostInstructions: '',
            entries: []
        });
        setCurrentGlossary(null);
        setView('editor');
    };

    const handleEdit = async (glossary) => {
        setIsLoading(true);
        try {
            let fullGlossary;
            if (activeTab === 'tenant') {
                fullGlossary = await glossaryService.getGlossary(tenantId, glossary.tenantGlossaryName);
            } else {
                fullGlossary = await glossaryService.getSharedGlossary(glossary.sharedGlossaryName);
            }

            setEditorForm({
                name: activeTab === 'tenant' ? fullGlossary.tenantGlossaryName : fullGlossary.sharedGlossaryName,
                displayName: fullGlossary.displayName || '',
                description: fullGlossary.description || '',
                hostInstructions: fullGlossary.hostInstructions || '',
                entries: fullGlossary.entries || []
            });
            setCurrentGlossary(fullGlossary);
            setView('editor');
        } catch (error) {
            setStatus({ type: 'error', message: 'Failed to load glossary details' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (name, displayName) => {
        setDeleteConfirm({ name, displayName: displayName || name });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const { name } = deleteConfirm;
        setDeleteConfirm(null);
        setIsLoading(true);
        try {
            if (activeTab === 'tenant') {
                await glossaryService.deleteGlossary(tenantId, name);
            } else {
                await glossaryService.deleteSharedGlossary(name);
            }
            setStatus({ type: 'success', message: 'Glossary deleted successfully' });
            loadGlossaries();
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        // Validate DisplayName
        const displayNameValidation = validateDisplayName(editorForm.displayName);
        if (!displayNameValidation.valid) {
            setStatus({ type: 'error', message: displayNameValidation.error });
            return;
        }

        // Validate shared glossary internal name
        if (activeTab === 'shared') {
            const nameValidation = validateSharedGlossaryName(editorForm.name);
            if (!nameValidation.valid) {
                setStatus({ type: 'error', message: nameValidation.error });
                return;
            }
        }

        setIsLoading(true);
        try {
            const isUpdate = !!currentGlossary;

            if (activeTab === 'tenant') {
                const payload = {
                    tenantId,
                    displayName: editorForm.displayName,
                    description: editorForm.description,
                    hostInstructions: editorForm.hostInstructions,
                    entries: editorForm.entries
                };

                // For updates, include the GUID in the payload
                if (isUpdate) {
                    payload.tenantGlossaryName = editorForm.name;
                    await glossaryService.updateGlossary(payload);
                } else {
                    await glossaryService.createGlossary(payload);
                }
            } else {
                const payload = {
                    sharedGlossaryName: editorForm.name,
                    displayName: editorForm.displayName,
                    description: editorForm.description,
                    hostInstructions: editorForm.hostInstructions,
                    entries: editorForm.entries
                };

                if (isUpdate) {
                    await glossaryService.updateSharedGlossary(editorForm.name, payload);
                } else {
                    await glossaryService.createSharedGlossary(payload);
                }
            }

            setStatus({ type: 'success', message: `Glossary ${isUpdate ? 'updated' : 'created'} successfully` });
            setView('list');
            loadGlossaries();
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddEntry = () => {
        if (!newEntry.sourceText || !newEntry.targetText) return;

        setEditorForm(prev => ({
            ...prev,
            entries: [...prev.entries, { ...newEntry, priority: 0, caseSensitive: false }]
        }));

        setNewEntry(prev => ({ ...prev, sourceText: '', targetText: '' }));
    };

    const handleRemoveEntry = (index) => {
        const entry = editorForm.entries[index];
        setEditorForm(prev => ({
            ...prev,
            entries: prev.entries.filter((_, i) => i !== index)
        }));
        setEditingEntryIdx(null);
        setEditingEntryData(null);
        // Show undo toast for 4 seconds
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setDeletedEntry({ data: entry, index });
        undoTimerRef.current = setTimeout(() => setDeletedEntry(null), 4000);
    };

    const handleUndoDelete = () => {
        if (!deletedEntry) return;
        clearTimeout(undoTimerRef.current);
        setEditorForm(prev => ({
            ...prev,
            entries: [
                ...prev.entries.slice(0, deletedEntry.index),
                deletedEntry.data,
                ...prev.entries.slice(deletedEntry.index)
            ]
        }));
        setDeletedEntry(null);
    };

    const handleStartEditEntry = (index) => {
        setEditingEntryIdx(index);
        setEditingEntryData({ ...editorForm.entries[index] });
    };

    const handleEditEntryField = (field, value) => {
        setEditingEntryData(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveEditEntry = () => {
        setEditorForm(prev => ({
            ...prev,
            entries: prev.entries.map((e, i) => i === editingEntryIdx ? { ...e, ...editingEntryData } : e)
        }));
        setEditingEntryIdx(null);
        setEditingEntryData(null);
    };

    const handleCancelEditEntry = () => {
        setEditingEntryIdx(null);
        setEditingEntryData(null);
    };

    const fileInputRef = useRef(null);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            try {
                // Format: Key=Value,Key=Value
                // Split by comma
                const items = content.split(',');
                const newEntries = [];

                items.forEach(item => {
                    const parts = item.split('=');
                    if (parts.length === 2) {
                        const sourceText = parts[0].trim();
                        const targetText = parts[1].trim();

                        if (sourceText && targetText) {
                            newEntries.push({
                                sourceLanguage: newEntry.sourceLanguage,
                                targetLanguage: newEntry.targetLanguage,
                                sourceText,
                                targetText,
                                priority: 0,
                                caseSensitive: false
                            });
                        }
                    }
                });

                if (newEntries.length > 0) {
                    setEditorForm(prev => ({
                        ...prev,
                        entries: [...prev.entries, ...newEntries]
                    }));
                    setStatus({ type: 'success', message: `Successfully added ${newEntries.length} entries.` });
                } else {
                    setStatus({
                        type: 'error',
                        message: 'No valid entries found. Please ensure the file uses "Source=Target" format separated by commas.'
                    });
                    setShowImportHelp(true); // Auto-show help on error
                }
            } catch (error) {
                console.error('Error parsing file:', error);
                setStatus({ type: 'error', message: 'Failed to parse file. Please check the file format.' });
                setShowImportHelp(true);
            }
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    // Render Helpers
    const renderList = () => (
        <>
            <div className="glossary-controls">
                {activeTab === 'tenant' && (
                    <div className="tenant-input-group">
                        <label className="ui-input-label">Tenant</label>
                        {isAdmin ? (
                            <select
                                className="ui-select"
                                value={tenantId}
                                onChange={(e) => setTenantId(e.target.value)}
                            >
                                <option value="">-- Select Tenant --</option>
                                {tenants.map(t => (
                                    <option key={t.RowKey} value={t.RowKey}>
                                        {t.TenantName}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className="tenant-name-display">
                                {user?.TenantName || tenantId || 'No Tenant Assigned'}
                            </div>
                        )}
                    </div>
                )}
                <button
                    className="refresh-icon-btn"
                    onClick={handleRefresh}
                    disabled={isLoading || (activeTab === 'tenant' && !tenantId)}
                    title={activeTab === 'tenant' && !tenantId ? 'Select a tenant first' : 'Refresh glossaries'}
                >
                    <RefreshCw size={20} className={isLoading ? 'rotating' : ''} />
                </button>
                <Button
                    onClick={handleCreateNew}
                    disabled={activeTab === 'tenant' && !tenantId}
                    title={activeTab === 'tenant' && !tenantId ? 'Select a tenant first' : 'Create a new glossary'}
                >
                    <Plus size={18} /> New Glossary
                </Button>
            </div>

            {isLoading ? (
                <div className="loading-state">
                    <Loader2 className="animate-spin" size={48} />
                    <p>Loading glossaries...</p>
                </div>
            ) : glossaries.length === 0 ? (
                <div className="empty-state">
                    <Book size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                    <h3>No Glossaries Found</h3>
                    <p>{activeTab === 'tenant' && !tenantId ? 'Select a tenant from the dropdown above' : 'Create a new glossary to get started'}</p>
                </div>
            ) : (
                <div className="glossary-grid">
                    {glossaries.map((g) => (
                        <div key={activeTab === 'tenant' ? g.tenantGlossaryName : g.sharedGlossaryName} className="glossary-card">
                            <div className="glossary-card-header">
                                <h3 className="glossary-card-title">
                                    {getGlossaryDisplayName(g)}
                                </h3>
                                <span className="badge">v{g.version}</span>
                            </div>
                            <div className="glossary-card-meta">
                                <span>{g.entryCount} entries</span>
                                <span>Updated: {new Date(g.updatedAt).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm text-secondary truncate">{g.description}</p>
                            <div className="glossary-card-actions">
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(g)}>
                                    <Edit2 size={16} /> Edit
                                </Button>
                                <Button variant="ghost" size="sm" className="text-error" onClick={() => handleDelete(activeTab === 'tenant' ? g.tenantGlossaryName : g.sharedGlossaryName, getGlossaryDisplayName(g))}>
                                    <Trash2 size={16} /> Delete
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'shared' && (
                <>
                    <hr className="dl-section-divider" />
                    <DomainLibrary
                        tenantId={tenantId}
                        isSuperAdmin={isSuperAdmin}
                        isAdmin={isAdmin}
                    />
                </>
            )}
        </>
    );

    const renderEditor = () => (
        <div className="glossary-editor">
            <div className="editor-header">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => setView('list')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <h2>{currentGlossary ? 'Edit Glossary' : 'New Glossary'}</h2>
                </div>
                <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setView('list')}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isLoading}>
                        <Save size={18} /> Save Changes
                    </Button>
                </div>
            </div>

            <div className="editor-section">
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <Input
                        label="Display Name *"
                        value={editorForm.displayName}
                        onChange={(e) => setEditorForm({ ...editorForm, displayName: e.target.value })}
                        placeholder="e.g., Medical Conference 2025, Legal Terms 📚"
                        helperText="User-friendly name (max 128 chars, any Unicode)"
                    />
                    {activeTab === 'shared' && (
                        <Input
                            label="Internal Name (Shared Glossary Name) *"
                            value={editorForm.name}
                            onChange={(e) => setEditorForm({ ...editorForm, name: e.target.value })}
                            disabled={!!currentGlossary} // Cannot change ID after creation
                            placeholder="e.g., medical-cardiology"
                            helperText="Lowercase alphanumeric with hyphens (max 50 chars)"
                        />
                    )}
                    <Input
                        label="Description"
                        value={editorForm.description}
                        onChange={(e) => setEditorForm({ ...editorForm, description: e.target.value })}
                        placeholder="Brief description of this glossary"
                    />
                </div>
                <div className="mb-4 host-instructions-container">
                    <label className="ui-input-label">Host Instructions (AI Context)</label>
                    <textarea
                        className="ui-input w-full host-instructions-textarea p-2"
                        value={editorForm.hostInstructions}
                        onChange={(e) => setEditorForm({ ...editorForm, hostInstructions: e.target.value })}
                        placeholder="Instructions for the AI translator..."
                    />
                </div>
            </div>

            <div className="editor-section">
                <h3>Entries ({editorForm.entries.length})</h3>

                <div className="entry-form">
                    <select
                        className="ui-select"
                        value={newEntry.sourceLanguage}
                        onChange={(e) => setNewEntry({ ...newEntry, sourceLanguage: e.target.value })}
                    >
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                    </select>
                    <select
                        className="ui-select"
                        value={newEntry.targetLanguage}
                        onChange={(e) => setNewEntry({ ...newEntry, targetLanguage: e.target.value })}
                    >
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                    </select>
                    <Input
                        placeholder="Source Text"
                        value={newEntry.sourceText}
                        onChange={(e) => setNewEntry({ ...newEntry, sourceText: e.target.value })}
                    />
                    <Input
                        placeholder="Target Text"
                        value={newEntry.targetText}
                        onChange={(e) => setNewEntry({ ...newEntry, targetText: e.target.value })}
                    />
                    <div className="flex gap-2 items-center">
                        <Button size="sm" onClick={handleAddEntry} disabled={!newEntry.sourceText || !newEntry.targetText}>
                            <Plus size={16} /> Add
                        </Button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".csv,.txt"
                            style={{ display: 'none' }}
                        />
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                            title="Import using selected languages (Format: Source=Target,Source=Target)"
                        >
                            <Upload size={16} /> Import CSV
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowImportHelp(!showImportHelp)}
                            title="Help with CSV Format"
                            className="help-btn"
                        >
                            <HelpCircle size={18} />
                        </Button>
                    </div>
                </div>

                <div className="entries-table-container">
                    <table className="entries-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Target</th>
                                <th>Original</th>
                                <th>Translation</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {editorForm.entries.map((entry, idx) => (
                                editingEntryIdx === idx ? (
                                    <tr key={idx} className="entry-editing-row">
                                        <td>
                                            <select className="entry-edit-select" value={editingEntryData.sourceLanguage} onChange={e => handleEditEntryField('sourceLanguage', e.target.value)}>
                                                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <select className="entry-edit-select" value={editingEntryData.targetLanguage} onChange={e => handleEditEntryField('targetLanguage', e.target.value)}>
                                                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <input className="entry-edit-input" value={editingEntryData.sourceText} onChange={e => handleEditEntryField('sourceText', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSaveEditEntry(); } else if (e.key === 'Escape') { e.preventDefault(); handleCancelEditEntry(); } }} />
                                        </td>
                                        <td>
                                            <input className="entry-edit-input" value={editingEntryData.targetText} onChange={e => handleEditEntryField('targetText', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSaveEditEntry(); } else if (e.key === 'Escape') { e.preventDefault(); handleCancelEditEntry(); } }} />
                                        </td>
                                        <td className="entry-edit-actions">
                                            <button type="button" className="action-icon-btn action-btn-save" onClick={handleSaveEditEntry} aria-label="Save entry" title="Save (Enter)"><CheckCircle size={16} aria-hidden="true" /></button>
                                            <button type="button" className="action-icon-btn action-btn-cancel" onClick={handleCancelEditEntry} aria-label="Cancel edit" title="Cancel (Esc)"><X size={16} aria-hidden="true" /></button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={idx}>
                                        <td>{LANGUAGES.find(l => l.code === entry.sourceLanguage)?.name || entry.sourceLanguage}</td>
                                        <td>{LANGUAGES.find(l => l.code === entry.targetLanguage)?.name || entry.targetLanguage}</td>
                                        <td>{entry.sourceText}</td>
                                        <td>{entry.targetText}</td>
                                        <td className="entry-edit-actions">
                                            <button type="button" className="action-icon-btn action-btn-edit" onClick={() => handleStartEditEntry(idx)} aria-label="Edit entry" title="Edit"><Edit2 size={16} aria-hidden="true" /></button>
                                            <button type="button" className="action-icon-btn action-btn-delete" onClick={() => handleRemoveEntry(idx)} aria-label="Delete entry" title="Delete"><Trash2 size={16} aria-hidden="true" /></button>
                                        </td>
                                    </tr>
                                )
                            ))}
                            {editorForm.entries.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 text-secondary">No entries yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    if (!getJwtToken()) {
        return (
            <div className="glossary-container">
                <div className="loading-state">
                    <Loader2 className="animate-spin" size={48} />
                    <p>Authenticating...</p>
                </div>
            </div>
        );
    }

    const backPath = location.state?.from || '/host';

    return (
        <div className="glossary-container">
            <div className="glossary-header">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" onClick={() => navigate(backPath)} title="Back">
                        <ArrowLeft size={24} />
                    </Button>
                    <h1>Glossary Management</h1>
                </div>

            </div>

            {
                status.message && (
                    <div className={`status-message ${status.type}`}>
                        {status.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                        {status.message}
                    </div>
                )
            }

            {
                (
                    <div className="glossary-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'tenant' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('tenant'); setView('list'); setGlossaries([]); }}
                        >
                            Tenant Glossaries
                        </button>
                        {(user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin') && (
                            <button
                                className={`tab-btn ${activeTab === 'shared' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('shared'); setView('list'); setGlossaries([]); }}
                            >
                                Shared Glossaries
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                className={`tab-btn ${activeTab === 'ai-generator' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('ai-generator'); setGlossaries([]); }}
                            >
                                <Sparkles size={16} /> AI Generator
                            </button>
                        )}
                    </div>
                )
            }

            {activeTab === 'ai-generator' ? (
                <AIGlossaryGenerator
                    tenantId={tenantId}
                    tenants={tenants}
                    isAdmin={isAdmin}
                    setTenantId={setTenantId}
                    onGlossarySaved={() => { setActiveTab('tenant'); loadGlossaries(); }}
                />
            ) : view === 'list' ? renderList() : renderEditor()}

            {deleteConfirm && (
                <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="confirm-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="confirm-modal-body">
                            <h3>Delete Glossary</h3>
                            <p>Are you sure you want to delete <strong>"{deleteConfirm.displayName}"</strong>? This action cannot be undone.</p>
                        </div>
                        <div className="confirm-modal-actions">
                            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                            <Button variant="danger" onClick={confirmDelete} disabled={isLoading}>
                                <Trash2 size={16} /> Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {deletedEntry && (
                <div className="undo-toast">
                    Entry removed
                    <button className="undo-toast-btn" onClick={handleUndoDelete}>Undo</button>
                </div>
            )}

            {showImportHelp && (
                <div className="modal-overlay" onClick={() => setShowImportHelp(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>CSV Upload Format Guide</h3>
                            <button className="close-btn" onClick={() => setShowImportHelp(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>To bulk import glossary entries, upload a text file (<code>.csv</code> or <code>.txt</code>) with the following rules:</p>
                            <ul className="help-list">
                                <li><strong>Important:</strong> The import uses the languages selected in the dropdowns above.</li>
                                <li>The <strong>Left side</strong> of your text = <strong>{LANGUAGES.find(l => l.code === newEntry.sourceLanguage)?.name}</strong> (Source).</li>
                                <li>The <strong>Right side</strong> of your text = <strong>{LANGUAGES.find(l => l.code === newEntry.targetLanguage)?.name}</strong> (Target).</li>
                                <li>Format each entry as: <code>{`{Source Text}={Target Text}`}</code></li>
                                <li>Separate multiple entries with commas <code>,</code></li>
                            </ul>
                            <div className="example-box">
                                <strong>Example:</strong>
                                <code>Hello=안녕하세요,World=세상,Friend=친구</code>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button onClick={() => setShowImportHelp(false)}>Got it</Button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default GlossaryManagement;
